import { useState, useEffect, useCallback } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Box from '@cloudscape-design/components/box';
import MixedLineBarChart from '@cloudscape-design/components/mixed-line-bar-chart';
import LineChart from '@cloudscape-design/components/line-chart';
import Spinner from '@cloudscape-design/components/spinner';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Button from '@cloudscape-design/components/button';
import { useNavigate } from 'react-router-dom';
import { TrendWeek } from '../types/ticket';
import { getTrends } from '../api/tickets';

type SeverityFilter = 'sev2' | 'all';

const SEVERITY_OPTIONS = [
  { id: 'sev2', text: 'SEV 2' },
  { id: 'all', text: 'All severities' },
];

const SEVERITY_MAP: Record<SeverityFilter, string[] | undefined> = {
  sev2: ['SEV_2'],
  all: undefined,
};

const TITLE_MAP: Record<SeverityFilter, string> = {
  sev2: 'Trends — SEV 2 (12 weeks)',
  all: 'Trends — All Severities (12 weeks)',
};

function formatWeekLabel(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function TrendCharts() {
  const navigate = useNavigate();
  const [data, setData] = useState<TrendWeek[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sevFilter, setSevFilter] = useState<SeverityFilter>('all');

  const fetchTrends = useCallback(async (filter: SeverityFilter) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTrends(SEVERITY_MAP[filter]);
      setData(res.weeks);
    } catch (e: any) {
      setData([]);
      setError(e.message || 'Failed to load trends');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrends(sevFilter);
  }, [sevFilter, fetchTrends]);

  const handleFilterChange = (id: string) => {
    setSevFilter(id as SeverityFilter);
  };

  if (loading) {
    return (
      <Container header={<Header variant="h2">{TITLE_MAP[sevFilter]}</Header>}>
        <Box textAlign="center" padding="xl">
          <Spinner size="large" />
        </Box>
      </Container>
    );
  }

  if (!data.length) {
    return (
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SegmentedControl
                selectedId={sevFilter}
                options={SEVERITY_OPTIONS}
                onChange={({ detail }) => handleFilterChange(detail.selectedId)}
              />
            }
          >
            {TITLE_MAP[sevFilter]}
          </Header>
        }
      >
        <Box color="text-body-secondary">
          {error ? `Error: ${error}` : 'No trend data available yet.'}
        </Box>
      </Container>
    );
  }

  const avgVolume = Math.round(data.reduce((sum, w) => sum + w.totalTickets, 0) / data.length);
  const xDomain = data.map((w) => formatWeekLabel(w.weekStart));

  const volumeSeries: any[] = [
    {
      title: 'Total Tickets',
      type: 'bar',
      data: data.map((w) => ({ x: formatWeekLabel(w.weekStart), y: w.totalTickets })),
      color: '#0972d3',
    },
    {
      title: 'High-Sev (SEV 1-2)',
      type: 'line',
      data: data.map((w) => ({ x: formatWeekLabel(w.weekStart), y: w.highSevTickets })),
      color: '#d91515',
    },
    {
      title: `Avg (${avgVolume}/wk)`,
      type: 'threshold',
      y: avgVolume,
      color: '#5f6b7a',
    },
  ];

  const mttrData = data.filter((w) => w.meanTimeToResolve !== null);
  const mttrSeries: any[] = [
    {
      title: 'MTTR (days)',
      type: 'line',
      data: mttrData.map((w) => ({ x: formatWeekLabel(w.weekStart), y: w.meanTimeToResolve! })),
      color: '#0972d3',
    },
    {
      title: 'Target (3 days)',
      type: 'threshold',
      y: 3,
      color: '#037f0c',
    },
  ];

  // Backlog Growth = created - resolved per week. Negative = shrinking (good), Positive = growing (bad)
  const backlogGrowthData = data.map((w) => ({
    x: formatWeekLabel(w.weekStart),
    y: w.totalTickets - w.resolvedTickets,
  }));

  const backlogGrowthSeries: any[] = [
    {
      title: 'Backlog Growth',
      type: 'bar',
      data: backlogGrowthData,
      color: '#0972d3',
    },
    {
      title: 'Zero (stable)',
      type: 'threshold',
      y: 0,
      color: '#037f0c',
    },
  ];

  const backlogValues = backlogGrowthData.map((d) => d.y);
  const mttrXDomain = mttrData.map((w) => formatWeekLabel(w.weekStart));

  // Map formatted week label back to the raw weekStart date for click navigation
  const labelToWeekStart = new Map(data.map((w) => [formatWeekLabel(w.weekStart), w.weekStart]));

  const navigateToWeek = (label: string, resolvedOnly = false) => {
    const weekStart = labelToWeekStart.get(label);
    if (!weekStart) return;
    const start = new Date(weekStart + 'T00:00:00Z');
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    const params = new URLSearchParams();
    params.set('dateRange', `absolute:${weekStart}:${end.toISOString().slice(0, 10)}`);
    if (resolvedOnly) params.set('statuses', 'Resolved,Closed');
    if (sevFilter === 'sev2') params.set('severities', 'SEV_2');
    navigate(`/tickets?${params.toString()}`);
  };

  const popoverFooter = (resolvedOnly: boolean) => (xValue: string) => (
    <Button
      variant="inline-link"
      iconName="external"
      onClick={() => navigateToWeek(String(xValue), resolvedOnly)}
    >
      View tickets for this week
    </Button>
  );

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SegmentedControl
              selectedId={sevFilter}
              options={SEVERITY_OPTIONS}
              onChange={({ detail }) => handleFilterChange(detail.selectedId)}
            />
          }
        >
          {TITLE_MAP[sevFilter]}
        </Header>
      }
    >
      <ColumnLayout columns={3}>
        <div>
          <Box variant="h4" margin={{ bottom: 'xxs' }}>Ticket Volume</Box>
          <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: 'xs' }}>
            New tickets created per week.
          </Box>
          <MixedLineBarChart
            series={volumeSeries}
            xDomain={xDomain}
            yDomain={[0, Math.max(...data.map((w) => w.totalTickets), 1) + 5]}
            yTitle="Tickets"
            height={220}
            hideFilter
            xScaleType="categorical"
            detailPopoverFooter={popoverFooter(false)}
            i18nStrings={{
              xTickFormatter: (v) => String(v),
              yTickFormatter: (v) => String(Math.round(Number(v))),
            }}
            empty={<Box>No data</Box>}
          />
        </div>
        <div>
          <Box variant="h4" margin={{ bottom: 'xxs' }}>Mean Time to Resolve (MTTR)</Box>
          <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: 'xs' }}>
            Avg. days from creation to resolution. Lower is better — target ≤ 3 days.
          </Box>
          {mttrData.length === 0 ? (
            <Box color="text-body-secondary" padding="l" textAlign="center">
              No resolved tickets in this period
            </Box>
          ) : (
            <LineChart
              series={mttrSeries}
              xDomain={mttrXDomain}
              yDomain={[0, Math.max(...mttrData.map((w) => w.meanTimeToResolve!), 5) + 1]}
              yTitle="Days"
              height={220}
              hideFilter
              xScaleType="categorical"
              detailPopoverFooter={popoverFooter(true)}
              i18nStrings={{
                xTickFormatter: (v) => String(v),
                yTickFormatter: (v) => String(Math.round(Number(v) * 10) / 10),
              }}
              empty={<Box>No data</Box>}
            />
          )}
        </div>
        <div>
          <Box variant="h4" margin={{ bottom: 'xxs' }}>Backlog Growth</Box>
          <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: 'xs' }}>
            Created minus resolved per week. Below zero = backlog shrinking. Above = growing.
          </Box>
          <MixedLineBarChart
            series={backlogGrowthSeries}
            xDomain={xDomain}
            yDomain={[Math.min(...backlogValues, 0) - 3, Math.max(...backlogValues, 0) + 3]}
            yTitle="Tickets"
            height={220}
            hideFilter
            xScaleType="categorical"
            detailPopoverFooter={popoverFooter(false)}
            i18nStrings={{
              xTickFormatter: (v) => String(v),
              yTickFormatter: (v) => String(Math.round(Number(v))),
            }}
            empty={<Box>No data</Box>}
          />
        </div>
      </ColumnLayout>
    </Container>
  );
}
